/**
 * Circle Tooltip Helper
 * 
 * Provides utility methods for creating professional tooltips and labels
 * for circle selection tool functionality.
 * 
 * This helper class extracts tooltip creation logic from MapComponent
 * to improve code organization and maintainability.
 */
export class CircleTooltipHelper {
    
    /**
     * Create professional-looking resize tooltip content with styling
     * Shows during circle resize with clean, simple white background
     * Matches Google Maps standard InfoWindow style
     * 
     * @param formattedRadius - Formatted radius string (e.g., "100.0 mi" or "100.0 mi (160.9 km)")
     * @returns HTML string with clean white background, subtle shadow
     */
    static createProfessionalTooltipContent(formattedRadius: string): string {
        return `
            <div class="circle-radius-label" style="
                background: #ffffff;
                color: #333333;
                padding: 4px 8px;
                border-radius: 2px;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
                font-family: Roboto, Arial, sans-serif;
                font-size: 13px;
                font-weight: 400;
                white-space: nowrap;
                line-height: 1.4;
            ">
                ${formattedRadius}
            </div>
        `;
    }
    
    /**
     * Create radius label content for permanent display at circle center
     * Shows formatted radius with clean, simple white background
     * Matches Google Maps standard InfoWindow style
     * 
     * @param radiusInMeters - Radius in meters
     * @param preferredUnit - User's preferred unit ('miles', 'kilometers', 'meters')
     * @param showBothUnits - Whether to show secondary unit in parentheses (default: false for cleaner display)
     * @returns HTML string with formatted radius label
     */
    static createRadiusLabelContent(radiusInMeters: number, preferredUnit: string = 'miles', showBothUnits: boolean = false): string {
        const formattedRadius = CircleTooltipHelper.formatRadiusForTooltip(radiusInMeters, preferredUnit, showBothUnits);
        
        return `
            <div class="circle-radius-label" style="
                background: #ffffff;
                color: #333333;
                padding: 4px 8px;
                border-radius: 2px;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
                font-family: Roboto, Arial, sans-serif;
                font-size: 13px;
                font-weight: 400;
                white-space: nowrap;
                line-height: 1.4;
            ">
                ${formattedRadius}
            </div>
        `;
    }
    
    /**
     * Format radius in meters to user-friendly display format
     * Shows radius in user's selected unit with optional secondary unit
     * 
     * @param radiusInMeters - Radius in meters
     * @param preferredUnit - User's preferred unit ('miles', 'kilometers', 'meters')
     * @param showBothUnits - Whether to show secondary unit in parentheses (default: true for backward compatibility)
     * @returns Formatted string with appropriate units
     */
    static formatRadiusForTooltip(radiusInMeters: number, preferredUnit: string = 'miles', showBothUnits: boolean = true): string {
        // Convert meters to different units
        const radiusInMiles = radiusInMeters / 1609.34;
        const radiusInKm = radiusInMeters / 1000;
        const radiusInFeet = radiusInMeters * 3.28084;
        
        // Format based on preferred unit
        switch (preferredUnit.toLowerCase()) {
            case 'kilometers':
            case 'km':
                if (radiusInKm < 1) {
                    // Small circles: show in meters
                    const metersFormatted = `${Math.round(radiusInMeters).toLocaleString()} M`;
                    if (showBothUnits) {
                        return `${metersFormatted} (${Math.round(radiusInFeet).toLocaleString()} FT)`;
                    }
                    return metersFormatted;
                } else if (radiusInKm < 10) {
                    const kmFormatted = `${radiusInKm.toFixed(1)} KM`;
                    if (showBothUnits) {
                        return `${kmFormatted} (${radiusInMiles.toFixed(1)} MI)`;
                    }
                    return kmFormatted;
                } else {
                    const kmFormatted = `${Math.round(radiusInKm).toLocaleString()} KM`;
                    if (showBothUnits) {
                        return `${kmFormatted} (${Math.round(radiusInMiles).toLocaleString()} MI)`;
                    }
                    return kmFormatted;
                }
                
            case 'meters':
            case 'm':
                const metersFormatted = `${Math.round(radiusInMeters).toLocaleString()} M`;
                if (showBothUnits) {
                    return `${metersFormatted} (${Math.round(radiusInFeet).toLocaleString()} FT)`;
                }
                return metersFormatted;
                
            case 'miles':
            case 'mi':
            default:
                if (radiusInMiles < 1) {
                    // Small circles: show in feet
                    const feetFormatted = `${Math.round(radiusInFeet).toLocaleString()} FT`;
                    if (showBothUnits) {
                        return `${feetFormatted} (${Math.round(radiusInMeters).toLocaleString()} M)`;
                    }
                    return feetFormatted;
                } else if (radiusInMiles < 10) {
                    const milesFormatted = `${radiusInMiles.toFixed(1)} MI`;
                    if (showBothUnits) {
                        return `${milesFormatted} (${radiusInKm.toFixed(1)} KM)`;
                    }
                    return milesFormatted;
                } else {
                    const milesFormatted = `${Math.round(radiusInMiles).toLocaleString()} MI`;
                    if (showBothUnits) {
                        return `${milesFormatted} (${Math.round(radiusInKm).toLocaleString()} KM)`;
                    }
                    return milesFormatted;
                }
        }
    }
}
